import type { PartyRole } from '../../status-colors.js';

/**
 * Avatar — the round initials marker that stands in for a person.
 *
 * Corpus: `DB:256`, the signed-in user in the app header (32px
 * circle, solid `#0e6b43`, white 12px/700, centred by inline-flex), and
 * `DB:1944`, a party in the case's parties panel (28px circle,
 * the role colour at full strength on a 13% tint of itself), drawn beside the
 * name and the role chip at `DB:729-731`. No file in the corpus
 * draws a photographic avatar, a status dot on one, or a stacked group, so
 * initials are the only form here and the other three would be invention.
 *
 * The two forms cut their letters differently, and the corpus is explicit about
 * both: the header takes the first letter of each of the first two words and
 * upper-cases the pair (`DB:2847`, "Sam Chen" → "SC"), while a
 * party row takes one character exactly as the name spells it
 * (`DB:1943`, `p.n.slice(0, 1)`). `tone` therefore selects a
 * form and not merely a palette: the header names the one person the reader is
 * signed in as, a party row labels a name already printed next to it.
 *
 * Two divergences, disclosed rather than smoothed over:
 *  - both corpus derivations index UTF-16 code units, which halves a surrogate
 *    pair and separates a combining mark from its base letter. This takes the
 *    first grapheme cluster instead: the same letter for every name in the
 *    corpus, and the right one for a name written in Devanagari or Thai.
 *  - `DB:256` hides the circle from assistive technology and
 *    carries the name in a `title` attribute, which is not reliably announced
 *    and is unreachable by keyboard. Where nothing else on screen names the
 *    person, this renders the name as screen-reader-only text instead;
 *    `decorative` is the party-row case, where the sibling already prints it.
 *
 * The name arrives as a prop and the letter is derived from it here, so the
 * derivation is pinned in one place rather than repeated at every call site.
 */
export interface AvatarProps {
  /** The person's full name. It is both the accessible name and the source of the letter. */
  readonly name: string;
  /** Overrides the derived letter for a name whose first character is not the one to show. */
  readonly initial?: string;
  readonly role?: PartyRole;
  /** Defaults to `role` when a role is given, and to `brand` when none is. */
  readonly tone?: 'brand' | 'role';
  readonly size?: 'sm' | 'md';
  /** True when a sibling already prints the name, as the parties panel does. */
  readonly decorative?: boolean;
}

const ROLE_CLASS: Record<PartyRole, string> = {
  tenant: 'rp-avatar--tenant',
  advocate: 'rp-avatar--advocate',
  property_manager: 'rp-avatar--manager',
  owner: 'rp-avatar--owner',
  tradie: 'rp-avatar--tradie',
};

const GRAPHEMES = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

/** One perceived character, which is not the same thing as one code unit. */
function firstGrapheme(word: string): string {
  return [...GRAPHEMES.segment(word)][0]?.segment ?? '';
}

function initialsOf(name: string, tone: 'brand' | 'role'): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  // An empty name draws an empty circle rather than a placeholder glyph,
  // because a stand-in letter names the wrong person.
  if (words.length === 0) return '';
  if (tone === 'role') return firstGrapheme(words[0] ?? '');
  // Locale-invariant, as the corpus is (DB:2847). The no-argument
  // `toLocaleUpperCase()` folds by the *host* locale, so the reader's machine
  // would decide the casing of somebody else's name: "Ida" is "ID" in Sydney
  // and "İD" in Istanbul, off the same record.
  return words.slice(0, 2).map(firstGrapheme).join('').toUpperCase();
}

export function Avatar({
  name,
  initial,
  role,
  tone,
  size = 'sm',
  decorative = false,
}: AvatarProps) {
  const form = tone ?? (role === undefined ? 'brand' : 'role');
  // A role tone with no role would leave the tint's custom property unset and
  // draw a transparent circle, so the whole form degrades to the brand one —
  // fill and letters together, rather than one letter on a green disc.
  const roleClass = form === 'role' && role !== undefined ? ROLE_CLASS[role] : undefined;
  const label = name.trim();
  const glyph = initial ?? initialsOf(name, roleClass === undefined ? 'brand' : 'role');
  // With no name there is nothing to announce, and an empty node in the
  // accessibility tree is a pause a screen-reader user has to interpret.
  const silent = decorative || label === '';
  const classes = [
    'rp-avatar',
    size === 'md' ? 'rp-avatar--md' : 'rp-avatar--sm',
    roleClass === undefined ? 'rp-avatar--brand' : 'rp-avatar--role',
    roleClass,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} aria-hidden={silent ? true : undefined}>
      <span className="rp-avatar__initial" aria-hidden="true">
        {glyph}
      </span>
      {silent ? null : <span className="rp-avatar__name">{label}</span>}
    </span>
  );
}
